import { getURLParams } from '../adapters/dupes/queryParams.js';

/**
 * Map from NYC county names to our formatted county names
 */
export const NYCCountyNames = {
    Kings: 'Kings (Brooklyn)',
    Queens: 'Queens',
    'New York': 'New York (Manhattan)',
    Bronx: 'Bronx',
    Richmond: 'Richmond (Staten Island)',
};

/**
 * Map from borough names to our formatted county names
 */
export const boroughs = {
    Brooklyn: 'Kings (Brooklyn)',
    Queens: 'Queens',
    Manhattan: 'New York (Manhattan)',
    Bronx: 'Bronx',
    'Staten Island': 'Richmond (Staten Island)',
};

/**
 *
 * @param url
 * @returns {string}
 */
export const getLocationText = function (url) {
    const params = getURLParams(url);
    if (params.cityName) {
        return params.cityName + ', ' + params.state;
    } else if (params.countyName) {
        return countyDisplayName(params.countyName, params.state);
    }
    return params.state || params.locationName || '';
};

/**
 * Keep this logic in sync with SearchManagerImpl.countyDisplayName
 * @param {string} rawCountyName
 * @param {string} state
 * @return {string}
 */
var countyDisplayName = function (rawCountyName, state) {
    return rawCountyName.replace(/county$/, state === 'LA' ? ' Parish' : ' County') +
        ', ' + state;
};

export const centerLocationParamNames = [
    'state',
    'cityName',
    'centerLat',
    'centerLng',
    'centerZip',
];

export const locationParamNames = centerLocationParamNames.concat([
    'countyName',
    'locationName',
    'includeNearbyLocations',
    'community',
    'school',
]);

/**
 * @returns {Record<string, string | nullish>}
 */
export const getEmptyLocationParams = function () {
    const params = {};
    locationParamNames.forEach(function (name) {
        params[name] = null;
    });
    return params;
};

/**
 * @returns {Record<string, string | nullish>}
 */
export const getBaseLocationParams = function () {
    const loc = getEmptyLocationParams();
    loc.includeNearbyLocations = true;
    return loc;
};

/**
 * @param {Object} maxMindLocation
 * @returns {string}
 */
export const makeSuggestionName = function (maxMindLocation) {
    const state = maxMindLocation.stateCode;
    const city = maxMindLocation.cityName;
    const county = maxMindLocation.countyName;

    if (city) {
        return city + ', ' + state;
    } else if (county) {
        return countyDisplayName(county, state);
    }
    return state;
};

/**
 * Converts a max mind location into a map of parameters suitable for a search URL
 *
 * TODO: we should consider what we really want to return if the max mind location
 * doesn't contain all the info we expect. Right now it should not error, but we
 * could end up with fewer filters than we wanted and thus broader location search.
 *
 * @param {!any} maxMindLocation The detected location object from Max Mind
 * @returns {Record<string, string | nullish>}
 */
export const extractSuggestionLocationParameters = function (maxMindLocation) {
    const stateCode = maxMindLocation.stateCode;
    const cityName = maxMindLocation.cityName;
    const borough = boroughs[cityName];
    const params = getBaseLocationParams();
    params.state = stateCode;

    if (stateCode === 'NY' && borough) {
        params.countyName = borough + 'county';
    } else {
        params.cityName = getAdjustedCityName(cityName, stateCode);
        params.countyName = maxMindLocation.countyName;
    }
    params.centerLat = maxMindLocation.latitude;
    params.centerLng = maxMindLocation.longitude;
    return params;
};

export const getAdjustedCityName = function (cityName, stateCode) {
    if (stateCode === 'NY') {
        if (cityName === 'New York') {
            return 'New York City';
        } else if (cityName === 'Cornwall-on-Hudson') {
            return 'Cornwall Hdsn';
        }
    }
    // We never use periods in our city names, but sometimes Google does
    // This is written to prevent errors on missing cityName, which occasionally happens
    return cityName && cityName.replace(/\./g, '');
};

/**
 * Extracts all the location-related parameters from a DC.org URL
 * @param {String} url
 * @returns {Object}
 */
export const extractURLLocationParameters = function (url) {
    const params = {};
    const oldParams = getURLParams(url);
    locationParamNames.forEach(function (name) {
        params[name] = oldParams[name];
    });
    return params;
};

/**
 * Used if the user wants to search for a specific location only
 * @param {string} url search URL
 * @returns {Object} Minimal parameters to search for the location without expansion
 */
export const removeLocationExpansionParams = function (url) {
    const params = getURLParams(url);
    const newParams = getEmptyLocationParams();

    if (params.centerZip) {
        newParams.centerZip = params.centerZip;
        // If zipOnly isn't set, we'll end up sorting all projects by proximity to this location instead of restricting to projects within the zip code
        newParams.zipOnly = true;
    } else if (params.cityName) {
        newParams.state = params.state;
        newParams.cityName = params.cityName;
    } else if (params.countyName) {
        newParams.state = params.state;
        newParams.countyName = params.countyName;
    } else if (params.state) {
        newParams.state = params.state;
    }

    return newParams;
};

export const removeLocationSpecificParamsForMapSearch = function (params) {
    const locationSpecificParams = {};
    if (params.centerZip) {
        locationSpecificParams.centerZip = params.centerZip;
        params.centerZip = null;
        params.zipOnly = false;
    }
    if (params.locationName) {
        locationSpecificParams.locationName = params.locationName;
        params.locationName = null;
    }
    if (params.cityName) {
        locationSpecificParams.cityName = params.cityName;
        params.cityName = null;
    }
    if (params.countyName) {
        locationSpecificParams.countyName = params.countyName;
        params.countyName = null;
    }
    if (params.state) {
        locationSpecificParams.state = params.state;
        params.state = null;
    }
    if (params.includeNearbyLocations) {
        params.includeNearbyLocations = false;
    }

    if (params.centerLat) {
        locationSpecificParams.centerLat = params.centerLat;
    }
    if (params.centerLng) {
        locationSpecificParams.centerLng = params.centerLng;
    }
    return locationSpecificParams;
};
